import { useMemo } from 'react';
import { useLocalState } from '@hooks/index';
// @ts-expect-error @types collides with node_modules/@types
import { FieldDefinitionFieldType, RowDataSourceTable } from '@types/graphql-global-types';
import { getFieldsAndDataSources_OpraQueries_fields_FieldDefinition } from '@queries/generated/central-monitoring.generated';

export type LocalStateFieldWithKeyAndName = getFieldsAndDataSources_OpraQueries_fields_FieldDefinition & {
    key: string;
    name: string;
};

export type UseLocalStateFieldsProps = {
    fieldTypes?: FieldDefinitionFieldType[];
    dataTypes?: RowDataSourceTable[];
    isHidden?: boolean;
    isCalculated?: boolean;
};

/**
 * Filters local state fields based on parameters.
 *
 * Fields are always filtered out if they are null or if either of their `key`/`name` properties are null.
 *
 * - `fieldTypes` - (optional). which field types to return. If omitted, all field types are returned
 *   - If defined, this should be memoized
 * - `dataTypes` - (optional). which data types to return. If omitted, all data types are returned
 *   - If defined, this should be memoized
 * - `isHidden` - (optional). whether to return hidden or non-hidden fields. If omitted, both are returned
 * - `isCalculated` - (optional). whether to return calculated or non-calculated fields. If omitted, both are returned
 */
export function useLocalStateFields(props?: UseLocalStateFieldsProps): LocalStateFieldWithKeyAndName[] {
    const fieldTypes = props?.fieldTypes;
    const dataTypes = props?.dataTypes;
    const isHidden = props?.isHidden;
    const isCalculated = props?.isCalculated;

    const { state } = useLocalState();

    return useMemo(() => {
        const fieldTypesSet = fieldTypes != null ? new Set<FieldDefinitionFieldType | null>(fieldTypes) : null;
        const dataTypesSet = dataTypes != null ? new Set<RowDataSourceTable | null>(dataTypes) : null;

        if (state?.fields == null) {
            return [];
        }

        return state.fields
            .filter((f) => f != null)
            .filter(
                (f) =>
                    (isHidden == null || f?.isHidden === isHidden) &&
                    (isCalculated == null || f?.isCalculated === isCalculated) &&
                    (fieldTypesSet == null || fieldTypesSet.has(f.fieldType)) &&
                    (dataTypesSet == null || dataTypesSet.has(f.dataType)),
            )
            .filter((f) => f.key != null && f.name != null) as LocalStateFieldWithKeyAndName[];
    }, [state?.fields, isHidden, isCalculated, fieldTypes, dataTypes]);
}
